import api from 'api';
import type { AnyAction } from 'redux';
import { combineReducers } from 'redux';
import type { Thunk } from 'types/redux';
import createAction from 'utils/create-action';
import {
  FETCHED_PROJECT_SUBSCRIPTIONS,
  FETCHED_PROJECT_SUBSCRIPTIONS_ERROR,
  FETCHING_PROJECT_SUBSCRIPTIONS,
  SUBSCRIBE,
  SUBSCRIBE_ERROR,
  UNSUBSCRIBE,
  UNSUBSCRIBE_ERROR,
} from './projectSubscriptions.constants';

export const fetching = createAction(FETCHING_PROJECT_SUBSCRIPTIONS);
export const fetched = createAction(FETCHED_PROJECT_SUBSCRIPTIONS);
export const fetchedError = createAction(FETCHED_PROJECT_SUBSCRIPTIONS_ERROR);
export const subscribe = createAction(SUBSCRIBE);
export const subscribeError = createAction(SUBSCRIBE_ERROR);
export const unsubscribe = createAction(UNSUBSCRIBE);
export const unsubscribeError = createAction(UNSUBSCRIBE_ERROR);

type ProjectSubscriptionStatus = 'default' | 'pending' | 'success' | 'error';

interface SubscriptionData {
  [id: number]: {
    subscribed: boolean;
  };
}

interface SubscriptionRequest {
  status: ProjectSubscriptionStatus;
  error?: {} | Error | null;
}

export interface ProjectSubscriptionsState {
  data: SubscriptionData;
  request: SubscriptionRequest;
}

export const getProjectSubscriptions =
  (projectId: number): Thunk<Promise<unknown>> =>
  dispatch => {
    dispatch(fetching());

    return api.getProjectSubscriptions(projectId).then(
      payload => {
        dispatch(
          fetched({
            projectId,
            payload,
          })
        );
      },
      error => {
        dispatch(
          fetchedError({
            error,
          })
        );
      }
    );
  };

export const subscribeToProject =
  (projectId: number): Thunk<Promise<unknown>> =>
  dispatch => {
    return api.subscribeToProject(projectId).then(
      payload => {
        dispatch(
          subscribe({
            projectId,
            payload,
          })
        );
      },
      error => {
        dispatch(
          subscribeError({
            error,
          })
        );
      }
    );
  };

export const unsubscribeFromProject =
  (projectId: number): Thunk<Promise<unknown>> =>
  dispatch => {
    return api.unsubscribeFromProject(projectId).then(
      () => {
        dispatch(
          unsubscribe({
            projectId,
          })
        );
      },
      error => {
        dispatch(
          unsubscribeError({
            error,
          })
        );
      }
    );
  };

export const initialRequestState: SubscriptionRequest = {
  status: 'default',
  error: null,
};

export const request = (
  state: SubscriptionRequest = initialRequestState,
  action: AnyAction
): SubscriptionRequest => {
  switch (action.type) {
    case FETCHING_PROJECT_SUBSCRIPTIONS:
      return {
        ...state,
        status: 'pending',
      };
    case FETCHED_PROJECT_SUBSCRIPTIONS:
      return {
        ...state,
        status: 'success',
      };
    case FETCHED_PROJECT_SUBSCRIPTIONS_ERROR:
      return {
        ...state,
        status: 'error',
        error: action.error,
      };
    default:
      return state;
  }
};

export const data = (state: SubscriptionData = {}, action: AnyAction): SubscriptionData => {
  switch (action.type) {
    case FETCHED_PROJECT_SUBSCRIPTIONS: {
      // The server returns an object of all subscriptions, but we only care if one of them is true | false
      // We also explicity check for true, instead of potentially returning true for truthy values
      const subscribed = Object.keys(action.payload).some(k => action.payload[k] === true);
      return {
        ...state,
        [action.projectId]: {
          ...state[action.projectId],
          subscribed,
        },
      };
    }
    case SUBSCRIBE: {
      return {
        ...state,
        [action.projectId]: {
          ...state[action.projectId],
          subscribed: true,
        },
      };
    }
    case UNSUBSCRIBE: {
      return {
        ...state,
        [action.projectId]: {
          ...state[action.projectId],
          subscribed: false,
        },
      };
    }
    default:
      return state;
  }
};

export default combineReducers({
  request,
  data,
});
